import type { FC, RefAttributes } from "react";
import { cn } from "@/lib/utils";
import { useLayoutEffect, useMemo, useRef, useState, type InputHTMLAttributes, type ReactNode } from "react";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  startAdornment?: ReactNode | ((value: InputHTMLAttributes<HTMLInputElement>["value"]) => ReactNode);
  endAdornment?: ReactNode | ((value: InputHTMLAttributes<HTMLInputElement>["value"]) => ReactNode);
  inputClassName?: string;
}

const Input: FC<InputProps & RefAttributes<HTMLInputElement>> = ({
  ref,
  className,
  type,
  startAdornment,
  endAdornment,
  inputClassName,
  value,
  ...props
}) => {
  const startAdornmentRef = useRef<HTMLInputElement | null>(null);
  const endAdornmentRef = useRef<HTMLInputElement | null>(null);

  const [inputPaddingLeft, setInputPaddingLeft] = useState<string>("0.5rem");
  const [inputPaddingRight, setInputPaddingRight] = useState<string>("0.5rem");

  const effectiveStartAdornment = useMemo(() => {
    if (typeof startAdornment === "function") {
      return startAdornment(value);
    } else {
      return startAdornment;
    }
  }, [startAdornment, value]);

  const effectiveEndAdornment = useMemo(() => {
    if (typeof endAdornment === "function") {
      return endAdornment(value);
    } else {
      return endAdornment;
    }
  }, [endAdornment, value]);

  useLayoutEffect(() => {
    const value = startAdornmentRef.current?.clientWidth ?? 0;

    setInputPaddingLeft(`calc(${value}px + 0.6rem)`);
  }, [startAdornmentRef.current?.clientWidth]);

  useLayoutEffect(() => {
    const value = endAdornmentRef.current?.clientWidth ?? 0;

    setInputPaddingRight(`calc(${value}px + 0.6rem)`);
  }, [endAdornmentRef.current?.clientWidth]);

  return (
    <div className={cn("relative w-auto", className)}>
      <div
        ref={startAdornmentRef}
        className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground w-auto block"
      >
        {effectiveStartAdornment}
      </div>
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background file:cursor-pointer file:border-0 file:bg-transparent file:text-sm file:font-medium text-foreground placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          inputClassName,
        )}
        style={{
          paddingLeft: inputPaddingLeft,
          paddingRight: inputPaddingRight,
        }}
        ref={ref}
        value={value}
        {...props}
      />
      <div
        ref={endAdornmentRef}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground w-auto block"
      >
        {effectiveEndAdornment}
      </div>
    </div>
  );
};
Input.displayName = "Input";

export { Input };
